import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { cn } from "@/lib/utils";
function DesktopMenu() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="hidden md:flex gap-8">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-4 w-16 bg-muted animate-pulse rounded-sm" />)}
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden md:flex">
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group relative flex flex-col items-center text-sm font-medium text-foreground/80 transition-colors hover:text-foreground font-mono lowercase tracking-wide">
                <span>{item.label}</span>
                <span className="absolute -bottom-2 h-1 w-1 rounded-full bg-primary opacity-0 transition-all duration-300 group-hover:opacity-100" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenu({
  isOpen,
  onClose
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);
  return <div className={cn("fixed inset-0 z-40 flex flex-col bg-background/95 backdrop-blur-xl transition-all duration-500 md:hidden", isOpen ? "opacity-100 visible" : "opacity-0 invisible pointer-events-none")}>
      <div className="container mx-auto px-6 py-24 flex flex-col items-center justify-center h-full space-y-8">
        {loading ? <div className="flex flex-col gap-6 items-center">
            {[1, 2, 3].map(i => <div key={i} className="h-6 w-32 bg-muted animate-pulse rounded-md" />)}
          </div> : <nav className="flex flex-col items-center gap-8">
            {menuItems.map((item, i) => <Link key={item.id} to={item.href || "#"} className="text-2xl font-mono lowercase text-foreground/90 hover:text-primary transition-colors" onClick={onClose} data-index={i}>
                {item.label}
              </Link>)}
          </nav>}
      </div>
    </div>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="header" className={cn("sticky top-0 z-50 w-full transition-all duration-500 border-b border-transparent", isScrolled ? "bg-background/80 backdrop-blur-md border-border/40 shadow-sm" : "bg-background border-transparent")}>
      <div className="container mx-auto px-4 h-16 md:h-20 flex items-center justify-between relative">
        {}
        <div className="md:hidden flex-1 flex justify-start">
          <Button variant="ghost" size="icon" className="relative z-50 hover:bg-transparent" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu">
            {isMobileMenuOpen ? <X className="h-6 w-6 text-foreground" /> : <Menu className="h-6 w-6 text-foreground" />}
          </Button>
        </div>

        {}
        <div className="flex-1 flex justify-center md:justify-start md:flex-none">
          <Link to="/" className="relative z-50 flex items-center gap-2" aria-label="OralGuide Home">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1556727/img/oralguide_logo.png" alt="OralGuide Logo" className="h-7 w-auto object-contain" />
          </Link>
        </div>

        {}
        <div className="hidden md:flex flex-1 justify-end">
          <MenuProvider menu_id="44">
            <DesktopMenu />
          </MenuProvider>
        </div>

        {}
        <div className="md:hidden flex-1" />
      </div>

      {}
      <MenuProvider menu_id="44">
        <MobileMenu isOpen={isMobileMenuOpen} onClose={() => setIsMobileMenuOpen(false)} />
      </MenuProvider>
    </header>;
}